import { ComponentType, createElement } from 'react';
import {
  CellEditingStoppedEvent,
  CellValueChangedEvent,
  ILargeTextEditorParams,
  INumberCellEditorParams,
  ISelectCellEditorParams,
  ISimpleFilterModelType,
  ValueFormatterFunc,
  CellRendererSelectorResult,
  ColDef,
  ICellRendererParams,
  IRowNode,
} from 'ag-grid-community';
import { FILTER_TYPES, TABLE_ROW_SELECTION } from '@/components/UI/TableListEditable/TableListEditableConstants';
import type { AlignValue, CellEditorType, FilterType } from '@/components/UI/TableListEditable/TableListEditableConstants';
import { TableListEditableMultiSelectionOption } from '@/components/UI/TableListEditable/TableListEditableMultiSelection';
import { ASC_ORDER, DESC_ORDER } from '@/helpers/constants';
import { Variables } from '@/types/EmailsEdition';

export type SortingDirection = typeof ASC_ORDER | typeof DESC_ORDER;

export type ColumnSorting = {
  columnName: string;
  direction: SortingDirection;
};

export type FilteringStateColumn = {
  columnName: string;
  filteringEnabled?: boolean;
};

export type Filter = {
  columnName: string;
  value?: string;
  operation?: string;
};

export type MultiSelectionColumnConfig = {
  options: TableListEditableMultiSelectionOption[];
  getAllSelectedData?: (selected: unknown[]) => TableListEditableMultiSelectionOption[];
  getOptionValue?: (option: TableListEditableMultiSelectionOption) => unknown;
  multiSelectionProps?: {
    maxChip?: number;
    withoutTranslations?: boolean;
    isOnlyPlants?: boolean;
    isModelSeries?: boolean;
    isPlants?: boolean;
    size?: 'small' | 'medium';
  };
  onChange?: (params: {
    rowData: Record<string, unknown>;
    columnName: string;
    value: unknown[];
  }) => void;
};

export type UsersSelectionColumnConfig = {
  name?: string;
  popupTitle?: string;
  buttonLabel?: string;
  isOnlyOneUserSelection?: boolean;
  onlyOneUserIsObject?: boolean;
  onChange?: (params: {
    rowData: Record<string, unknown>;
    columnName: string;
    value: unknown;
  }) => void;
};

export type InputTextColumnConfig = {
  name: string;
  maxLength?: number;
  onChange?: (params: {
    rowData: Record<string, unknown>;
    columnName: string;
    value: string;
  }) => void;
  onBlur?: (params: {
    rowData: Record<string, unknown>;
    columnName: string;
    value: string;
  }) => void;
};

export type OptionsCustomColumnConfig = {
  defaultOptions?: Variables[];
  componentProps: Record<string, unknown>;
  onChange?: (params: {
    rowData: Record<string, unknown>;
    columnName: string;
    value: unknown;
  }) => void;
};

export type TreeDataConfig<RowData extends Record<string, unknown>> = {
  childrenProperty: keyof RowData & string;
  columnExpanded: keyof RowData & string;
  childrenNotSelectable?: boolean;
  innerCellRenderer?: ComponentType<ICellRendererParams<RowData>>;
};

export const booleanCellRenderer = ({ value }: { value: boolean }) => (
  createElement('i', {
    className: value
      ? 'di icon-kreis-check Green DialogColumnMargin'
      : 'di icon-ablehnen Red DialogColumnMargin',
  })
);

export type ColumnConfig = {
  name: string;
  title: string;
  showMenu?: boolean;
  wrapText?: boolean;
  editable?: ColDef['editable'];
  flex?: number;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  pinned?: 'left' | 'right' | boolean;
  align?: AlignValue;
  popupTitle?: string;
  cellRenderer?: string | ((params: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any;
    data: Record<string, unknown>;
  }) => React.ReactNode);
  cellRendererSelector?: (params: {
    value: unknown;
    data: Record<string, unknown>;
  }) => CellRendererSelectorResult | undefined;
  cellEditor?: CellEditorType;
  cellEditorPopup?: boolean;
  cellEditorParams?: ILargeTextEditorParams | INumberCellEditorParams | ISelectCellEditorParams;
  filters?: ISimpleFilterModelType[];
  filterType?: FilterType;
  filterComponent?: React.ComponentType<any> | string;
  filterParams?: Record<string, unknown>;
  floatingFilterComponent?: React.ComponentType<any> | string;
  floatingFilterComponentParams?: Record<string, unknown>;
  filterValueGetter?: (params: {
    value: unknown;
    data: Record<string, unknown>;
  }) => unknown;
  valueFormatter?: ValueFormatterFunc;
  suppressMovable?: boolean;
  suppressFloatingFilterButton?: boolean;
  suppressHeaderFilterButton?: boolean;
  multiSelection?: MultiSelectionColumnConfig;
  optionsCustom?: OptionsCustomColumnConfig;
  usersSelection?: Omit<UsersSelectionColumnConfig, 'name'>;
  inputText?: Omit<InputTextColumnConfig, 'name'>;
  tooltipValueGetter?: (params: { value: unknown; data: Record<string, unknown> }) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  headerComponent?: React.ComponentType<any> | string;
  headerComponentParams?: Record<string, unknown>;
};

export const getColumnFilterType = (column: ColumnConfig) => column.filterType ?? FILTER_TYPES.TEXT;

export type TableListEditableNewProps<RowData extends Record<string, unknown>> = {
  list: RowData[];
  columns: ColumnConfig[];
  pageSizes?: number[];
  columnSorting?: ColumnSorting[];
  onSortModelChange?: (sorting: ColumnSorting[]) => void;
  filteringStateColumns?: FilteringStateColumn[];
  isFiltering?: boolean;
  filterModel?: Record<string, unknown>;
  onFilterModelChange?: (filterModel: Record<string, unknown>) => void;
  loading?: boolean;
  hiddenColumns?: string[];
  hasCheckbox?: boolean;
  columnId?: string;
  selection?: string[] | number[];
  rowSelectionMode?: typeof TABLE_ROW_SELECTION[keyof typeof TABLE_ROW_SELECTION];
  onSelectionChange?: (selectedRows: unknown[]) => void;
  onCellValueChanged?: (params: CellValueChangedEvent) => void;
  onCellEditingStopped?: (params: CellEditingStoppedEvent) => void;
  isPaginated?: boolean;
  onPageSizeChange?: (pageSize: number) => void;
  onPageChange?: (pageIndex: number) => void;
  tableHeight?: number | string;
  showColumnVisibilityButton?: boolean;
  resetFilters?: string;
  usersColumns?: string[];
  usersColumnsContent?: UsersSelectionColumnConfig[];
  hasMinWidth?: boolean;
  treeDataConfig?: TreeDataConfig<RowData>;
  validationStatus?: Record<string, Record<string, { isValid?: boolean }>>;
  expandedRows?: (string | number)[];
  isRowSelectable?: (node: IRowNode<RowData>) => boolean;
  allowColumnMove?: boolean;
  animateRows?: boolean;
  onColumnWidthsChange?: (widths: Record<string, number>) => void;
  onColumnsOrderChanged?: (order: string[]) => void;
  onColumnVisibilityChanged?: (hidden: string[]) => void;
  onResetColumnsSettings?: () => void;
};
